import {
  alpha,
  Box,
  Card,
  Rating,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PosterTile from "../../components/PosterTile";
import UserChip from "../../components/UserChip";
import { getStatusOptions } from "../../utils/utils";

/**
 * Activity card for Explore carousels showing a user's rating/review of a piece of content.
 * Props match the shape returned by GET /feed (journal activity items).
 *
 * onPosterClick: called when the poster is long-pressed (touch) or clicked (desktop) → opens LibraryModal.
 *                Tapping the poster instead reveals PosterTile's status overlay.
 * onCardClick:   called when the card body is tapped → opens LibraryModal on that entry.
 *
 * Its hover/tap scale comes from the Carousel it sits in (see itemScale).
 */
export default function ActivityCarouselCard({
  content_id,
  content_type,
  content_title,
  image_url,
  username,
  avatar_url,
  rating,
  notes,
  entry_status,
  onPosterClick,
  onCardClick,
}) {
  const theme = useTheme();
  const status = getStatusOptions(theme)[entry_status];

  return (
    <Card
      elevation={1}
      sx={{
        width: { xs: "270px", sm: "300px" },
        flexShrink: 0,
        scrollSnapAlign: "start",
        bgcolor: (t) => alpha(t.palette.secondary.main, 0.5),
        cursor: "pointer",
        // Suppress the native touch highlight so long-pressing the poster
        // (to open the LibraryModal) doesn't flash the whole card.
        WebkitTapHighlightColor: "transparent",
        backgroundImage: "none", // MUI's dark-mode Paper overlay would otherwise lighten this on top of the custom bgcolor above — keep the elevation shadow, drop the tint.
      }}
      onClick={onCardClick}
    >
      <Box sx={{ p: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {/* Poster — PosterTile owns the gesture handling: tap reveals the
              status overlay, long press (touch) / click (desktop) calls onClick.
              These stops keep the poster's interactions from bubbling up to the
              card body — onPointerDown also stops the Carousel's whileTap
              press-in on this card, which listens on pointer events and isn't
              covered by the mouse/touch stops alone. */}
          <Box
            sx={{ width: 98, flexShrink: 0 }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <PosterTile
              contentId={content_id}
              contentType={content_type}
              contentTitle={content_title}
              imageUrl={image_url}
              onClick={onPosterClick}
              padding={0.5}
              showStatus="full"
              inline
              overlay
            />
          </Box>

          {/* Metadata */}
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ pt: 0.5 }}
            >
              <UserChip
                username={username}
                avatarUrl={avatar_url}
                interactive={false}
              />
              {/* Status icon sits inline next to the user chip. */}
              {status ? (
                <Box
                  component="span"
                  aria-label={status.label}
                  sx={{
                    display: "inline-flex",
                    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                  }}
                >
                  {status.icon}
                </Box>
              ) : null}
            </Stack>

            {rating ? <Rating value={rating} precision={0.5} readOnly /> : null}

            {notes ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                }}
              >
                {notes}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
}
